<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue'
  import { useRouter } from 'vue-router'
  import {
    UsersService,
    OrganisationsService,
    type CustomerUser,
    type OrganisationEmployee,
    RoleEnum,
    FlagEnum
  } from '@/types/generated'
  import { TableCell, TableRow } from '@/components/ui/table'
  import { Button } from '@/components/ui/button'
  import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription
  } from '@/components/ui/dialog'
  import { useTranslation } from '@/composables/useTranslation'
  import { genericErrorToast } from '@/lib/toasts'
  import { UserPlus, Pencil, Trash2, AlertTriangle } from 'lucide-vue-next'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
  import PaginatedTableView, {
    type FetchDataOptions,
    type PaginatedResponse,
    type Filter
  } from '@/components/admin/PaginatedTable.vue'
  import TableOptionsButton from '@/components/admin/table/TableOptionsButton.vue'
  import { ROUTES } from '@/router/routes'
  import { useFeatureFlag } from '@/composables/useFeatureFlag'

  interface EmployeeWithRole extends OrganisationEmployee {
    role: RoleEnum
  }

  const router = useRouter()
  const currentUser = ref<CustomerUser | null>(null)
  const employeeCount = ref(0)
  const isInitialLoading = ref(true)
  const hasLoadError = ref(false)
  const selectedEmployee = ref<EmployeeWithRole | null>(null)
  const showDeleteModal = ref(false)
  const paginatedTableRef = ref<InstanceType<typeof PaginatedTableView> | null>(null)

  const { t } = useTranslation({
    en: {
      unauthorized: 'Unauthorized Access',
      unauthorizedMessage: 'You do not have permission to view this page.',
      employees: 'Employees',
      addEmployee: 'Add Employee',
      name: 'Name',
      email: 'Email',
      role: 'Role',
      actions: 'Actions',
      edit: 'Edit',
      delete: 'Delete',
      cancel: 'Cancel',
      confirm: 'Confirm',
      deleteEmployee: 'Delete Employee',
      deleteConfirm: 'Are you sure you want to delete this employee? This action cannot be undone.',
      editEmployee: 'Edit Employee',
      createEmployee: 'Create New Employee',
      firstName: 'First Name',
      lastName: 'Last Name',
      password: 'Password',
      newPassword: 'New Password',
      changePassword: 'Change Password',
      save: 'Save',
      seatLimitReached:
        'To add more employees, your law firm needs additional seats. Please contact support.',
      updatePhoto: 'Click to update profile photo',
      lawyer: 'Lawyer',
      admin: 'Admin',
      clerk: 'Clerk',
      phoneAiUser: 'Phone AI user',
      passwordManagement: 'Manage employee password',
      passwordSecurityNote: 'Make sure to use a strong password',
      basicInformation: 'Basic Information',
      employeeRole: 'Employee Role',
      enterNewPassword: 'Enter new password',
      allRoles: 'All Roles',
      filterByRole: 'Role',
      loadError: 'Failed to load user data. Please try again.'
    },
    de: {
      unauthorized: 'Nicht autorisiert',
      unauthorizedMessage: 'Sie haben keine Berechtigung, diese Seite anzuzeigen.',
      employees: 'Mitarbeiter',
      addEmployee: 'Mitarbeiter hinzufügen',
      name: 'Name',
      email: 'E-Mail',
      role: 'Rolle',
      actions: 'Aktionen',
      edit: 'Bearbeiten',
      delete: 'Löschen',
      cancel: 'Abbrechen',
      confirm: 'Bestätigen',
      deleteEmployee: 'Mitarbeiter löschen',
      deleteConfirm:
        'Sind Sie sicher, dass Sie diesen Mitarbeiter löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      editEmployee: 'Mitarbeiter bearbeiten',
      createEmployee: 'Neuen Mitarbeiter erstellen',
      firstName: 'Vorname',
      lastName: 'Nachname',
      password: 'Passwort',
      newPassword: 'Neues Passwort',
      changePassword: 'Passwort ändern',
      save: 'Speichern',
      seatLimitReached:
        'Um weitere Mitarbeiter hinzuzufügen benötigt Ihre Kanzlei weitere Sitze. Bitte kontaktieren Sie den Support.',
      updatePhoto: 'Klicken Sie, um das Profilfoto zu aktualisieren',
      lawyer: 'Anwalt',
      admin: 'Administrator',
      clerk: 'Sachbearbeiter',
      phoneAiUser: 'Telefon-KI-Nutzer',
      passwordManagement: 'Passwort verwalten',
      passwordSecurityNote: 'Stellen Sie sicher, dass Sie ein sicheres Passwort verwenden',
      basicInformation: 'Grundinformationen',
      employeeRole: 'Mitarbeiterrolle',
      enterNewPassword: 'Neues Passwort eingeben',
      allRoles: 'Alle Rollen',
      filterByRole: 'Rolle',
      loadError: 'Benutzerdaten konnten nicht geladen werden. Bitte versuchen Sie es erneut.'
    },
    fr: {
      unauthorized: 'Accès non autorisé',
      unauthorizedMessage: "Vous n'avez pas la permission de consulter cette page.",
      employees: 'Collaborateurs',
      addEmployee: 'Ajouter un collaborateur',
      name: 'Nom',
      email: 'E-mail',
      role: 'Rôle',
      actions: 'Actions',
      edit: 'Modifier',
      delete: 'Supprimer',
      cancel: 'Annuler',
      confirm: 'Confirmer',
      deleteEmployee: 'Supprimer le collaborateur',
      deleteConfirm:
        'Êtes-vous sûr de vouloir supprimer ce collaborateur ? Cette action est irréversible.',
      editEmployee: 'Modifier le collaborateur',
      createEmployee: 'Créer un nouveau collaborateur',
      firstName: 'Prénom',
      lastName: 'Nom',
      password: 'Mot de passe',
      newPassword: 'Nouveau mot de passe',
      changePassword: 'Changer le mot de passe',
      save: 'Enregistrer',
      seatLimitReached:
        'Pour ajouter plus de collaborateurs, votre cabinet a besoin de places supplémentaires. Veuillez contacter le support.',
      updatePhoto: 'Cliquez pour modifier la photo de profil',
      lawyer: 'Avocat',
      admin: 'Administrateur',
      clerk: 'Collaborateur',
      phoneAiUser: 'Utilisateur IA téléphonique',
      passwordManagement: 'Gérer le mot de passe',
      passwordSecurityNote: "Assurez-vous d'utiliser un mot de passe fort",
      basicInformation: 'Informations de base',
      employeeRole: 'Rôle du collaborateur',
      enterNewPassword: 'Entrer un nouveau mot de passe',
      allRoles: 'Tous les rôles',
      filterByRole: 'Rôle',
      loadError: "Les données utilisateur n'ont pas pu être chargées. Veuillez réessayer."
    }
  })

  const isAdmin = computed(() => {
    return currentUser.value?.role === RoleEnum.ADMIN
  })

  const canAddMoreEmployees = computed(() => {
    return employeeCount.value < (currentUser.value?.org?.seats || 0)
  })

  const phonebotOnlyRoleFlag = useFeatureFlag(FlagEnum.PHONEBOT_ONLY_ROLE)

  const filterableFields = computed<Filter[]>(() => {
    const roleOptions: { value: string; label: string }[] = [
      { value: 'all', label: t('allRoles') },
      { value: 'ADMIN', label: t('admin') },
      { value: 'LAWYER', label: t('lawyer') },
      { value: 'CLERK', label: t('clerk') }
    ]
    if (phonebotOnlyRoleFlag.isEnabled.value) {
      roleOptions.push({ value: 'PHONE_AI_USER', label: t('phoneAiUser') })
    }
    return [
      {
        key: 'role',
        label: t('filterByRole'),
        type: 'select' as const,
        value: 'all',
        options: roleOptions
      }
    ]
  })

  const getRoleDisplayText = (role: string | null | undefined, isLawfirmAdmin: boolean): string => {
    if (role === 'ADMIN' || (!role && isLawfirmAdmin)) return t('admin')
    if (role === 'LAWYER') return t('lawyer')
    if (role === 'CLERK') return t('clerk')
    if (role === 'PHONE_AI_USER') return t('phoneAiUser')
    return t('lawyer') // fallback
  }

  onMounted(async () => {
    phonebotOnlyRoleFlag.checkFlag()

    try {
      currentUser.value = await UsersService.usersRetrieve({ uuid: 'current' })
    } catch (error) {
      console.error('Failed to fetch user:', error)
      hasLoadError.value = true
      genericErrorToast()
    } finally {
      isInitialLoading.value = false
    }
  })

  const fetchEmployees = async (
    options?: FetchDataOptions
  ): Promise<PaginatedResponse<OrganisationEmployee>> => {
    const orgUuid = currentUser.value?.org?.uuid
    if (!orgUuid) {
      return { results: [], next: null, previous: null, count: 0 }
    }

    const filters = options?.filters || []
    const roleFilter = filters.find((filter) => filter.key === 'role')?.value

    // Pass role filter to API (undefined if 'all' selected)
    const role = roleFilter && roleFilter !== 'all' ? roleFilter : undefined

    const response = await OrganisationsService.organisationsEmployeesList({
      uuid: orgUuid,
      limit: options?.limit,
      offset: options?.offset,
      search: options?.search,
      role
    } as any) // TODO: regenerate types to include 'role' parameter

    // Handle both array and paginated response formats
    const results = Array.isArray(response) ? response : (response as any).results || []
    const totalCount = Array.isArray(response)
      ? response.length
      : (response as any).count || results.length

    // Store count for seat limit check
    employeeCount.value = totalCount

    return {
      results,
      next: Array.isArray(response) ? null : ((response as any).next ?? null),
      previous: Array.isArray(response) ? null : ((response as any).previous ?? null),
      count: totalCount
    }
  }

  const openDeleteModal = (employee: OrganisationEmployee) => {
    selectedEmployee.value = {
      ...employee,
      role: (employee as any).role || (employee.is_lawfirm_admin ? RoleEnum.ADMIN : RoleEnum.LAWYER)
    }
    showDeleteModal.value = true
  }

  const isDeleting = ref(false)

  const handleDelete = async () => {
    const orgUuid = currentUser.value?.org?.uuid
    if (!selectedEmployee.value || !orgUuid) return

    try {
      isDeleting.value = true
      await OrganisationsService.organisationsEmployeesRemoveCreate({
        employeeUuid: selectedEmployee.value.uuid,
        uuid: orgUuid
      })
      paginatedTableRef.value?.refreshData()
      showDeleteModal.value = false
      selectedEmployee.value = null
    } catch (error) {
      console.error('Failed to delete employee:', error)
      genericErrorToast()
    } finally {
      isDeleting.value = false
    }
  }
</script>
<template>
  <SettingsPage>
    <template #actions>
      <Button
        v-if="isAdmin"
        data-cy="add-employee-button"
        variant="default"
        class="inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-3 py-2
          text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus:ring-2
          focus:ring-purple-500 disabled:pointer-events-none disabled:opacity-50"
        :disabled="!canAddMoreEmployees"
        @click="router.push(ROUTES.APP.SETTINGS.LAWFIRM.TEAM_CREATE)"
      >
        <UserPlus class="h-4 w-4" />
        {{ t('addEmployee') }}
      </Button>
    </template>

    <div
      v-if="isInitialLoading"
      class="flex items-center justify-center py-12"
    >
      <div class="space-y-4 text-center">
        <div
          class="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-purple-600"
        ></div>
        <p class="text-gray-500">Loading...</p>
      </div>
    </div>

    <div
      v-else-if="hasLoadError"
      class="flex flex-col items-center justify-center py-12"
    >
      <AlertTriangle class="mb-4 h-12 w-12 text-yellow-500" />
      <p class="text-gray-600">{{ t('loadError') }}</p>
    </div>

    <div
      v-else-if="!isAdmin"
      class="flex flex-col items-center justify-center py-12"
    >
      <AlertTriangle class="mb-4 h-12 w-12 text-yellow-500" />
      <h2 class="mb-2 text-2xl font-bold">{{ t('unauthorized') }}</h2>
      <p class="text-gray-600">{{ t('unauthorizedMessage') }}</p>
    </div>

    <div
      v-else
      class="space-y-4"
    >
      <!-- Seat limit warning -->
      <div
        v-if="!canAddMoreEmployees"
        class="border-l-4 border-yellow-400 bg-yellow-50 p-4"
      >
        <div class="flex">
          <AlertTriangle class="mr-2 h-5 w-5 text-yellow-400" />
          <p class="text-sm text-yellow-700">
            {{ t('seatLimitReached') }}
          </p>
        </div>
      </div>

      <PaginatedTableView
        ref="paginatedTableRef"
        searchable-fields="name, email"
        :fetch-data="fetchEmployees"
        :filterable-fields="filterableFields"
      >
        <template #table-head>
          <TableCell class="w-16 pl-4"></TableCell>
          <TableCell class="w-1/3">{{ t('name') }}</TableCell>
          <TableCell class="w-1/3">{{ t('email') }}</TableCell>
          <TableCell class="w-32">{{ t('role') }}</TableCell>
          <TableCell class="w-16"></TableCell>
        </template>
        <template #table-row="{ items }">
          <TableRow
            v-for="employee in items"
            :key="employee.uuid"
            :data-cy="`employee-row-${employee.uuid}`"
            class="group hover:bg-gray-50"
          >
            <TableCell class="w-16 py-4 pl-4">
              <Avatar class="h-9 w-9">
                <AvatarImage
                  v-if="employee.avatar"
                  :src="employee.avatar"
                  :alt="employee.first_name"
                />
                <AvatarFallback class="bg-purple-100 text-sm text-purple-600">
                  {{ employee.first_name?.[0] }}{{ employee.last_name?.[0] }}
                </AvatarFallback>
              </Avatar>
            </TableCell>
            <TableCell class="w-1/3 py-4">
              <span
                class="block truncate font-medium text-gray-900"
                :title="`${employee.first_name} ${employee.last_name}`"
              >
                {{ employee.first_name }} {{ employee.last_name }}
              </span>
            </TableCell>
            <TableCell class="w-1/3 py-4">
              <span
                class="block truncate text-gray-600"
                :title="employee.email"
              >
                {{ employee.email }}
              </span>
            </TableCell>
            <TableCell class="w-32 py-4">
              <span
                :class="[
                  'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                  employee.is_lawfirm_admin
                    ? 'bg-purple-100 text-purple-800'
                    : 'bg-gray-100 text-gray-800'
                ]"
              >
                {{ getRoleDisplayText((employee as any).role, employee.is_lawfirm_admin) }}
              </span>
            </TableCell>
            <TableCell class="w-16 py-4">
              <TableOptionsButton
                :data-cy="`employee-options-button-${employee.uuid}`"
                :options="[
                  {
                    label: t('edit'),
                    dataCy: `employee-edit-${employee.uuid}`,
                    onClick: () =>
                      router.push(
                        ROUTES.APP.SETTINGS.LAWFIRM.TEAM_EDIT.replace(':uuid', employee.uuid)
                      ),
                    icon: Pencil
                  },
                  {
                    label: t('delete'),
                    dataCy: `employee-delete-${employee.uuid}`,
                    onClick: () => openDeleteModal(employee),
                    icon: Trash2
                  }
                ]"
              />
            </TableCell>
          </TableRow>
        </template>
      </PaginatedTableView>

      <!-- Delete Confirmation Modal -->
      <Dialog
        :open="showDeleteModal"
        @update:open="showDeleteModal = false"
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{{ t('deleteEmployee') }}</DialogTitle>
            <DialogDescription>{{ t('deleteConfirm') }}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="secondary"
              @click="showDeleteModal = false"
            >
              {{ t('cancel') }}
            </Button>
            <Button
              variant="destructive"
              :disabled="isDeleting"
              @click="handleDelete"
            >
              {{ isDeleting ? '...' : t('confirm') }}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  </SettingsPage>
</template>
